import { KeyboardEvent, useRef, useState } from 'react';
import {
  AlertTriangle,
  LoaderCircle,
  MinusCircleIcon,
  PlusCircleIcon,
  Keyboard,
  Layers,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import {
  DISPOSITION_COLORS,
  PALLET_BUILD_OPERATION_TYPES,
  PALLET_LICENSE_STATUS_TYPES,
  SCREEN_TYPES,
} from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { useAuthStore } from 'store/authStore';
import { buildPallet } from 'services/api/palletBuild';
import { playCallbackSound, playErrorSound, playGoodSound } from 'utils/sounds';
import { isValidLicenseId } from 'utils/validation';

export const AssignCasesPage = () => {
  const [scanValue, setScanValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cartonNotFound, setCartonNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const { pallet_id, pallet, setPallet } = usePalletBuildStore();
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [loading, setLoading] = useState(false);

  const isRemoveMode = mode === 'remove';
  const isPalletNew = pallet?.license_status === PALLET_LICENSE_STATUS_TYPES.NEW;
  const isPalletClosed = pallet?.license_status === PALLET_LICENSE_STATUS_TYPES.CLOSED;
  const displayCartons = pallet?.palletDetail ?? [];

  const handleSubmit = async () => {
    if (isPalletClosed) return;
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setScanValue('');
    setHasError(false);
    setCartonNotFound(false);
    const failureMessage = `Failed to ${isRemoveMode ? 'remove' : 'add'} case. Please try again.`;

    if (!isValidLicenseId(trimmed)) {
      setErrorMessage('Case ID must be 20 digits.');
      setHasError(true);
      playErrorSound();
      return;
    }

    setLoading(true);
    try {
      const user_id = useAuthStore.getState().user?.id ?? '';
      const result = await buildPallet({
        operation_id: isRemoveMode
          ? PALLET_BUILD_OPERATION_TYPES.REMOVE
          : PALLET_BUILD_OPERATION_TYPES.ASSIGN,
        user_id,
        license_id: pallet_id!,
        carton_id: trimmed,
      });
      if (!result || result.responseState !== 'confirmed') {
        setErrorMessage(result?.responseMsg || failureMessage);
        setHasError(true);
        playErrorSound();
        return;
      }
      setPallet(result);
      isRemoveMode ? playCallbackSound() : playGoodSound();
      toast.success(isRemoveMode ? 'Case removed from pallet' : 'Case added to pallet');
    } catch {
      setErrorMessage(failureMessage);
      setHasError(true);
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleDone = () => {
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
  };

  return (
    <div
      className={cn(
        '-mx-4 -mb-6 flex min-h-full flex-col gap-4 px-6 pb-6 pt-4',
        isRemoveMode || hasError
          ? DISPOSITION_COLORS.PROBLEM.gradient
          : DISPOSITION_COLORS.CONFIRMING.gradient
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-white/80">
          Pallet Build · Step 2
        </span>
        <h2 className="text-2xl font-bold text-white">Assign Cases</h2>
        {isPalletClosed ? (
          <p className="text-3xl font-bold text-white text-center">
            Pallet is closed — cases cannot be added or removed
          </p>
        ) : (
          hasError &&
          !cartonNotFound && (
            <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>
          )
        )}
      </div>

      {cartonNotFound && (
        <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-white" />
          <div className="flex flex-1 flex-col min-w-0">
            <span className="text-sm font-bold text-white">Case not found</span>
            <span className="text-xs text-white/80">Create the case first, then come back.</span>
          </div>
          <Button
            type="button"
            onClick={() => setCurrentScreen(SCREEN_TYPES.PALLET_BUILD_CASE_SCAN)}
            className="h-9 shrink-0 rounded-lg bg-white px-3 text-xs font-bold text-red-700 hover:bg-white/90"
          >
            Go to Manage Case
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
          <Layers className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            Pallet
          </span>
          <span className="font-mono text-sm font-bold text-slate-800 truncate">
            {pallet_id ?? '—'}
          </span>
        </div>
        {isPalletNew && (
          <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
            New
          </span>
        )}
      </div>

      <div
        className={cn(
          'flex items-center gap-3 rounded-xl px-4 py-3',
          isRemoveMode ? 'bg-rose-50' : 'bg-emerald-50'
        )}
      >
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            isRemoveMode ? 'bg-rose-100' : 'bg-emerald-100'
          )}
        >
          {isRemoveMode ? (
            <MinusCircleIcon className="h-4 w-4 text-rose-600" />
          ) : (
            <PlusCircleIcon className="h-4 w-4 text-emerald-600" />
          )}
        </div>
        <span
          className={cn(
            'text-xl font-bold uppercase tracking-widest',
            isRemoveMode ? 'text-rose-600' : 'text-emerald-600'
          )}
        >
          {isRemoveMode ? 'Remove Case' : 'Add Case'}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Assigned Cases
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">
            {displayCartons?.length || 0}
          </span>
        </div>

        {displayCartons.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-4 py-8">
            <Package className="h-8 w-8 text-slate-200" />
            <p className="text-sm text-slate-400">No cases assigned yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {displayCartons.map((carton) => (
              <div key={carton.carton_id} className="flex items-center justify-between px-4 py-3">
                <span className="font-mono text-sm font-medium text-slate-700 truncate">
                  {carton.carton_id}
                </span>
                <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                  {carton.product_cnt}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-slate-50 py-3">
          <LoaderCircle className="h-5 w-5 animate-spin text-emerald-600" />
          <span className="text-sm font-medium text-slate-500">
            {isRemoveMode ? 'Removing case...' : 'Adding case...'}
          </span>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Case Barcode</Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Scan case..."
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            disabled={isPalletClosed}
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            disabled={isPalletClosed}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container !bg-transparent">
        <Button
          variant="outline"
          onClick={() => setMode(isRemoveMode ? 'add' : 'remove')}
          className={cn('flex-1 h-12', {
            'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700': isRemoveMode,
            'text-rose-600 hover:bg-rose-50 hover:text-rose-700': !isRemoveMode,
          })}
        >
          Switch to {isRemoveMode ? 'Add' : 'Remove'}
        </Button>
        <Button
          onClick={handleDone}
          disabled={loading}
          className="bg-emerald-600 text-white hover:bg-emerald-700 flex-1 h-12"
        >
          Done
        </Button>
      </div>
    </div>
  );
};
